'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useGetInfiniteAgentsQuery } from '@/api/agents/queries/get-agents-query'
import { CreateEmptyBrandMutation } from '@/api/brands/mutations/create-empty-brand-mutation'
import { useBrandContext } from '@/features/brands/brand-context-definition'
import {
    canRecoverOnboardingChat,
    createOnboardingChatRecovery,
    type IOnboardingRecoveryStatus,
} from '@/features/onboarding/utils/onboarding-chat-recovery'
import { resolveDashboardAgentRedirect } from '@/features/onboarding/utils/dashboard-agent-redirect'
import { Route } from '@/setup/routing/routing'
import { useBrandRoute } from '@/setup/routing/use-brand-router'
import { useApiMutation } from '@/utils/api/api-mutation'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'

/** Resolves the existing chat first; only an explicit click may repair a missing one. */
export function useOnboardingChatRecovery(isSidebarLocked: boolean) {
    const { api, apiParams } = useApiToolbox()
    const { brand } = useBrandContext()
    const { getRoute } = useBrandRoute()
    const agentsQuery = useGetInfiniteAgentsQuery({ enabled: isSidebarLocked })
    const agents = agentsQuery.data?.pages.flatMap((page) => page?.data ?? []) ?? []
    const agentUuid = resolveDashboardAgentRedirect({
        agents,
        isOnboardingActive: true,
        shouldOpenLatest: false,
    })
    const onboardingChatUrl =
        isSidebarLocked && brand && agentUuid ? getRoute(Route.AGENT_TASK).getUrl({ agent_uuid: agentUuid }) : null
    const canRecoverOnboarding = canRecoverOnboardingChat({
        isSidebarLocked,
        emptyBrand: brand?.emptyBrand,
        agentsStatus: agentsQuery.status,
        isFetching: agentsQuery.isFetching,
        agentCount: agents.length,
    })

    // No cache seeding is needed: recovery enters a fresh route tree. Explicit request scope also
    // prevents an organization switch during the request from seeding the new workspace's cache.
    const { mutateAsync } = useApiMutation({
        mutationFn: (request: { organizationId: string; idempotencyKey: string }) =>
            CreateEmptyBrandMutation.call(api, {
                organizationId: request.organizationId,
                body: { idempotencyKey: request.idempotencyKey },
            }),
    })
    const organizationId = apiParams.organizationId
    const scope = `${organizationId}:${apiParams.brandSlug}`
    const currentRef = useRef({ scope, onboardingChatUrl, canRecoverOnboarding })
    currentRef.current = { scope, onboardingChatUrl, canRecoverOnboarding }
    const mountedRef = useRef(false)
    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    const [attempt, setAttempt] = useState<{ scope: string; status: IOnboardingRecoveryStatus } | null>(null)
    const openOnboardingChat = useMemo(
        () =>
            createOnboardingChatRecovery({
                getChatUrl: () => currentRef.current.onboardingChatUrl,
                canRecover: () => Boolean(organizationId) && currentRef.current.canRecoverOnboarding,
                isCurrentScope: () => mountedRef.current && currentRef.current.scope === scope,
                createIdempotencyKey: () => crypto.randomUUID(),
                recover: (idempotencyKey) => mutateAsync({ organizationId, idempotencyKey }),
                // A recovered target can belong to a sibling brand. The current brand's intercepted
                // task slot must not keep its old providers around the destination chat.
                navigate: (url) => window.location.assign(url),
                setStatus: (status) => setAttempt({ scope, status }),
            }),
        [mutateAsync, organizationId, scope]
    )

    return {
        onboardingChatUrl,
        canRecoverOnboarding,
        openOnboardingChat,
        onboardingRecoveryStatus: attempt?.scope === scope ? attempt.status : ('idle' as const),
    }
}
